import { useState } from 'react';
import { Anchor, Button, Card, Code, Group, Text, Title } from '@mantine/core';

/**
 * The code an editor pastes into the article.
 *
 * Copying can be refused (an older browser, or a page without permission), so there is always the
 * visible code to select by hand as well. The block keeps a monospace face, wraps rather than
 * scrolling out of sight, and selects in one gesture on a triple click.
 */
export function EmbedSnippet({ snippet, liveUrl }: { snippet: string; liveUrl?: string }) {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  async function copy() {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  }

  return (
    <Card component="section" withBorder radius="lg" p="lg" mt="xl">
      <Title order={2} size="h3">
        Embed code
      </Title>
      <Text size="sm" c="dimmed" mt={4}>
        Paste this into the article where the poll should appear. It works in any content management
        system that accepts an HTML or embed block.
      </Text>
      <Code block mt="md" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
        {snippet}
      </Code>
      <Group mt="md" gap="md">
        <Button variant="default" onClick={() => void copy()}>
          Copy
        </Button>
        {copyState === 'copied' && (
          <Text size="sm" c="violet" role="status">
            Copied to your clipboard.
          </Text>
        )}
        {copyState === 'failed' && (
          <Text size="sm" c="dimmed" role="status">
            Your browser would not let us copy. Select the code above and copy it yourself.
          </Text>
        )}
      </Group>
      {liveUrl !== undefined && (
        <Text size="sm" c="dimmed" mt="sm">
          Preview the live poll at{' '}
          <Anchor href={liveUrl} target="_blank" rel="noreferrer" size="sm">
            {liveUrl}
          </Anchor>
          .
        </Text>
      )}
    </Card>
  );
}
